import { AuthErrorKind, AuthFlow, AuthFlowError, Locales } from '@griply/types';
import { getAuthErrorCode } from '@griply/utils';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getGlobalAllowedSearchParameters } from 'shared/lib/react-router-dom';
import { getErrorReporter } from 'shared/services/error-tracking';
import { User } from 'shared/types/user';

import { claimSignup } from './claim-signup';
import { createPushToken } from './create-push-token';
import { finishUserSignup } from './finish-user-signup';
import { getPushToken } from './get-push-token';
import { getUser } from './get-user';
import { getAuthentication, handleAuthError } from './helpers';

// With the password length checked in the form, the claim endpoint's 400 always means a spent link.
const DEAD_CLAIM_CODE = 'functions/invalid-argument';

export const completeSignupAndLogin = async (
  claimToken: string,
  password: string,
  { name, locale }: { name?: string; locale?: Locales },
) => {
  const auth = await getAuthentication();

  try {
    const { email } = await claimSignup(claimToken, password);
    await signInWithEmailAndPassword(auth, email, password);

    const notificationsEnabled =
      'Notification' in window && Notification.permission === 'granted';

    await finishUserSignup({
      uid: auth.currentUser?.uid,
      name,
      locale,
      hasNotifications: notificationsEnabled,
      queryParameters: Object.fromEntries(getGlobalAllowedSearchParameters()),
    });

    // Signup already completed; a failed profile read must not surface as a signup failure.
    let user: User | null = null;

    try {
      user = await getUser();

      if (user && notificationsEnabled) {
        const token = await getPushToken(user.uid);
        token && (await createPushToken(user.uid, token, locale));
      }
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'complete-signup-post-auth-user-read' },
      });
    }

    return user;
  } catch (e) {
    if (getAuthErrorCode(e) === DEAD_CLAIM_CODE) {
      throw new AuthFlowError(AuthErrorKind.InvalidLink, { cause: e });
    }

    throw handleAuthError(e, AuthFlow.CompleteSignup);
  }
};
